import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, ApiError } from "@/api/client";
import type { Appointment, Product, Settlement } from "@/api/types";
import { Badge, Button, Chip, Field, Input, Select, tx } from "@/components/ui";
import { ProductLines, type ProductLine, linesTotal } from "@/components/ProductLines";

type Line = { method: string; amount: string };
type Props = { appointment: Appointment; tz: string; onDone: () => void; onCancel: () => void };
const money = (v: string | number) => (Math.round(Number(v) * 100) / 100).toFixed(2);
const localInput = (iso: string, tz: string) => {
  const d = new Date(iso);
  const parts = new Intl.DateTimeFormat("en-GB", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }).formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get("minute")}`;
};
/** What a coupon takes off an amount — mirrors the server (percent capped at the amount; fixed amount capped too). */
const discountOf = (c: { kind: string; value: string } | null, amount: number) => {
  if (!c || amount <= 0) return 0;
  const v = Number(c.value);
  return Math.min(amount, c.kind === "percent" ? Math.round(amount * v) / 100 : v);
};

/** Settlement of a visit (P10): Original amount (price + add-ons, editable) − one discount (profile or coupon) =
 * Amount paid (editable, e.g. 34.90 → 35), plus products sold at the same time; payments cover paid + products − deposit. */
export function CheckoutDialog({ appointment: a, tz, onDone, onCancel }: Props) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["settlement", a.id], queryFn: () => api<Settlement>(`/app/appointments/${a.id}/settlement`) });
  const [type, setType] = useState<string>("paid");
  const [addons, setAddons] = useState<string[]>([]);
  const [original, setOriginal] = useState(money(a.price_quoted));
  const [discountKey, setDiscountKey] = useState<string>(""); // "d:<discount id>" | "c:<coupon id>" | ""
  const [paid, setPaid] = useState(money(a.price_quoted));
  const [points, setPoints] = useState("");
  const [paidTouched, setPaidTouched] = useState(false);
  const [products, setProducts] = useState<ProductLine[]>([]);
  const [lines, setLines] = useState<Line[]>([]);
  const [pkgId, setPkgId] = useState("");
  const [when, setWhen] = useState(localInput(new Date().toISOString(), tz));
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const s = q.data;
  useEffect(() => {
    if (!s) return;
    const initial = s.service_kind === "trial" ? "trial" : s.package || s.usable_packages.length ? "credit" : "paid";
    setType(initial);
    setPkgId(s.package?.id ?? s.usable_packages[0]?.id ?? "");
    const method = s.payment_methods[0] ?? "cash";
    setLines([{ method, amount: money(Math.max(0, Number(a.price_quoted) - Number(s.deposit_paid))) }]);
    const first = s.pending_discounts.find((d) => d.usable);
    if (first) setDiscountKey(`d:${first.id}`);
  }, [s, a.price_quoted]);
  // original = quoted + the add-ons ticked; re-derived whenever add-ons change, still editable by hand
  const addonTotal = useMemo(() => (s?.addons ?? []).filter((x) => addons.includes(x.id)).reduce((acc, x) => acc + Number(x.price), 0), [s, addons]);
  useEffect(() => { setOriginal(money(Number(a.price_quoted) + addonTotal)); }, [a.price_quoted, addonTotal]);
  const chosen = useMemo(() => {
    if (!s || !discountKey) return null;
    if (discountKey.startsWith("d:")) return s.pending_discounts.find((d) => `d:${d.id}` === discountKey)?.coupon ?? null;
    return s.coupons.find((c) => `c:${c.id}` === discountKey) ?? null;
  }, [s, discountKey]);
  const discount = discountOf(chosen, Number(original));
  const rw = s?.rewards;
  const pts = Math.max(0, Math.floor(Number(points || 0)));
  const pointsValue = rw ? Math.min(Math.max(0, Number(original) - discount), Math.round(pts * Number(rw.point_value) * 100) / 100) : 0;
  const maxPoints = rw ? Math.min(rw.balance, Number(rw.point_value) > 0 ? Math.floor(Math.max(0, Number(original) - discount) / Number(rw.point_value)) : 0) : 0;
  useEffect(() => { if (!paidTouched) setPaid(money(Math.max(0, Number(original) - discount - pointsValue))); }, [original, discount, pointsValue, paidTouched]);
  const deposit = Number(s?.deposit_paid ?? 0);
  const productsTotal = linesTotal(products);
  const payable = useMemo(() => Math.max(0, (type === "paid" ? Number(paid) : 0) + productsTotal - (type === "paid" ? deposit : 0)), [type, paid, productsTotal, deposit]);
  useEffect(() => { if (lines.length === 1) setLines([{ ...lines[0], amount: money(payable) }]); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [payable]);
  const sum = lines.reduce((acc, l) => acc + Number(l.amount || 0), 0);
  const diff = Math.round((payable - sum) * 100) / 100;
  const needPayments = type === "paid" || productsTotal > 0;
  const m = useMutation({
    mutationFn: () => api<Settlement>(`/app/appointments/${a.id}/checkout`, { method: "POST", body: JSON.stringify({
      settlement_type: type,
      price_original: type === "paid" ? original : null,
      price_charged: type === "paid" ? paid : null,
      addons: (s?.addons ?? []).filter((x) => addons.includes(x.id)).map((x) => ({ addon_id: x.id, name: x.name, price: x.price })),
      customer_discount_id: type === "paid" && discountKey.startsWith("d:") ? discountKey.slice(2) : null,
      coupon_id: type === "paid" && discountKey.startsWith("c:") ? discountKey.slice(2) : null,
      redeem_points: type === "paid" ? pts : 0,
      product_lines: products.filter((l) => l.qty > 0).map((l) => ({ product_id: l.product.id, qty: l.qty, unit_price: l.unit_price })),
      payments: needPayments ? lines.filter((l) => Number(l.amount) > 0) : [],
      customer_package_id: type === "credit" && pkgId ? pkgId : null, checkout_at: when ? new Date(when + ":00").toISOString() : null, note: note || null,
    }) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["calendar"] }); qc.invalidateQueries({ queryKey: ["checkout-issues"] }); qc.invalidateQueries({ queryKey: ["products"] }); onDone(); },
    onError: (e) => { let reason = e instanceof ApiError ? e.detail : ""; try { reason = JSON.parse(reason).reason; } catch { /* plain */ } setError(t(`checkout.err.${reason}`, { defaultValue: reason || t("app.error") })); },
  });
  if (!s) return <p className="text-sm text-muted">{t("app.loading")}</p>;
  const setLine = (i: number, patch: Partial<Line>) => setLines(lines.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const couponLabel = (c: { code: string | null; name: Record<string, string>; kind: string; value: string }) => `${tx(c.name, i18n.language) || c.code || ""}${c.code ? ` (${c.code})` : ""} · ${c.kind === "percent" ? `−${Number(c.value)}%` : `−${money(c.value)} €`}`;
  return (
    <form onSubmit={(e) => { e.preventDefault(); setError(null); m.mutate(); }} className="space-y-3 rounded-lg border p-3 text-sm">
      <div className="font-medium">{t("checkout.title")}</div>
      <Field label={t("checkout.settlement")}>
        <Select value={type} onChange={(e) => setType(e.target.value)}>
          <option value="paid">{t("checkout.type.paid")}</option>
          <option value="credit" disabled={!s.package && s.usable_packages.length === 0}>{t("checkout.type.credit")}{!s.package && s.usable_packages.length === 0 ? ` (${t("checkout.noCredits")})` : ""}</option>
          <option value="trial">{t("checkout.type.trial")}</option>
          <option value="complimentary">{t("checkout.type.complimentary")}</option>
          {s.can_mark_test_visit && <option value="test_visit">{t("checkout.type.test_visit")}</option>}
        </Select>
      </Field>
      {s.addons.length > 0 && (
        <Field label={t("checkout.addons")} hint={t("checkout.addonsHint")}>
          <div className="flex flex-wrap gap-1">{s.addons.map((x) => <Chip key={x.id} active={addons.includes(x.id)} onClick={() => setAddons(addons.includes(x.id) ? addons.filter((i) => i !== x.id) : [...addons, x.id])}>{tx(x.name, i18n.language)} +{money(x.price)} €</Chip>)}</div>
        </Field>
      )}
      {type === "paid" && (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Field label={t("checkout.original")} hint={t("checkout.originalHint")}><Input type="number" step="0.01" min="0" value={original} onChange={(e) => setOriginal(e.target.value)} /></Field>
            <Field label={t("checkout.discount")}>
              <Select value={discountKey} onChange={(e) => { setDiscountKey(e.target.value); setPaidTouched(false); }}>
                <option value="">{t("checkout.noDiscount")}</option>
                {s.pending_discounts.map((d) => <option key={d.id} value={`d:${d.id}`} disabled={!d.usable}>★ {couponLabel(d.coupon)}{d.note ? ` · ${d.note}` : ""}</option>)}
                {s.coupons.map((c) => <option key={c.id} value={`c:${c.id}`}>{couponLabel(c)}</option>)}
              </Select>
            </Field>
            <Field label={t("checkout.amountPaid")} hint={discount > 0 ? t("checkout.discountLine", { amount: money(discount) }) : t("checkout.amountPaidHint")}><Input type="number" step="0.01" min="0" value={paid} onChange={(e) => { setPaid(e.target.value); setPaidTouched(true); }} /></Field>
            <div className="text-xs text-muted">{t("checkout.deposit")}<br /><b>{money(deposit)} €</b></div>
          </div>
          {rw?.enabled && rw.balance > 0 && (
            <div className="flex flex-wrap items-center gap-2 rounded-lg bg-accent-soft px-3 py-2 text-xs">
              <span className="font-medium text-accent-ink">{t("rw.usePoints")}</span>
              <span className="text-muted">{t("rw.balanceShort", { n: rw.balance })}{rw.service_price_points ? ` · ${t("rw.servicePriceShort", { n: rw.service_price_points })}` : ""}</span>
              <Input className="!w-24" type="number" min={0} max={maxPoints} step={1} value={points} onChange={(e) => { setPoints(e.target.value); setPaidTouched(false); }} />
              <button type="button" className="underline" onClick={() => { setPoints(String(maxPoints)); setPaidTouched(false); }}>{t("rw.max")}</button>
              {rw.service_price_points && rw.balance >= rw.service_price_points && <button type="button" className="underline" onClick={() => { setPoints(String(Math.min(maxPoints, rw.service_price_points!))); setPaidTouched(false); }}>{t("rw.payService")}</button>}
              {pts > 0 && <span className={pts < rw.min_redeem ? "text-danger" : ""}>= −{money(pointsValue)} €{pts < rw.min_redeem ? ` · ${t("rw.minRedeemShort", { n: rw.min_redeem })}` : ""}</span>}
            </div>
          )}
        </div>
      )}
      {type === "credit" && (
        <Field label={t("checkout.package")}>
          {s.package ? <div className="rounded-lg bg-surface-2 px-3 py-1.5">{tx(s.package.name, i18n.language)} · {t("checkout.creditsLeft", { n: s.package.credits_left, total: s.package.credits_total })} · {t("checkout.alreadyBurned")}</div>
            : <Select value={pkgId} onChange={(e) => setPkgId(e.target.value)}>{s.usable_packages.map((p) => <option key={p.id} value={p.id}>{tx(p.name, i18n.language)} · {p.credits_left} · {new Date(p.expires_at).toLocaleDateString(i18n.language)}</option>)}</Select>}
        </Field>
      )}
      {type === "test_visit" && <p className="text-xs text-warn">{t("checkout.testVisitHint")}</p>}
      <ProductLines brandId={a.brand_id} lines={products} onChange={setProducts} />
      {needPayments && (
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs text-muted"><span>{t("checkout.payable")}{productsTotal > 0 ? ` (${t("checkout.inclProducts", { amount: money(productsTotal) })})` : ""}</span><b className="text-sm text-ink">{money(payable)} €</b></div>
          {lines.map((l, i) => (
            <div key={i} className="flex items-center gap-2">
              <Select className="!w-32" value={l.method} onChange={(e) => setLine(i, { method: e.target.value })}>{s.payment_methods.map((pm) => <option key={pm} value={pm}>{t(`checkout.method.${pm}`)}</option>)}</Select>
              <Input className="!w-28" type="number" step="0.01" min="0" value={l.amount} onChange={(e) => setLine(i, { amount: e.target.value })} />
              {lines.length > 1 && <button type="button" className="text-xs" onClick={() => setLines(lines.filter((_, j) => j !== i))}>✕</button>}
            </div>
          ))}
          <div className="flex items-center gap-3 text-xs">
            <button type="button" className="underline" onClick={() => setLines([...lines, { method: s.payment_methods[1] ?? s.payment_methods[0] ?? "cash", amount: money(Math.max(0, diff)) }])}>{t("checkout.split")}</button>
            {Math.abs(diff) > 0.004 && <Badge tone="amber">{diff > 0 ? t("checkout.missing", { amount: money(diff) }) : t("checkout.over", { amount: money(-diff) })}</Badge>}
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        <Field label={t("checkout.time")}><Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} /></Field>
        <Field label={t("appt.notes")}><Input value={note} onChange={(e) => setNote(e.target.value)} /></Field>
      </div>
      {error && <p className="text-danger" role="alert">{error}</p>}
      <div className="flex gap-2">
        <Button disabled={m.isPending || (needPayments && Math.abs(diff) > 0.004) || (type === "credit" && !s.package && !pkgId)} title={needPayments && Math.abs(diff) > 0.004 ? t("checkout.mustBalance") : undefined}>{t("checkout.confirm")}</Button>
        <Button type="button" variant="ghost" onClick={onCancel}>{t("app.cancel")}</Button>
      </div>
    </form>
  );
}

export type { Product };
